/**
 * Presentation of numbers — the one place where locale, currency symbol and the
 * "hide balances" switch are applied. The metric engine deals only in raw
 * numbers; everything a person reads is produced here.
 */

/** Currencies whose sign is unambiguous enough to print instead of the code. */
const SIGNED_CURRENCIES = new Set(['USD', 'EUR', 'GBP']);

export function createFormatter({
  lang, baseCurrency = 'PLN', hideBalances, unitSystem = 'metric', t
}) {
  const locale = lang === 'PL' ? 'pl-PL' : 'en-US';

  /*
   * `Intl` knows each currency's symbol, its placement and how many decimals it
   * conventionally carries — which is exactly the knowledge a hand-kept symbol
   * table gets wrong the moment somebody adds a currency nobody anticipated.
   * Formatters are cached because constructing one is not cheap.
   */
  const formatters = new Map();
  const formatterFor = (code, decimals) => {
    const key = code + ':' + decimals;
    if (!formatters.has(key)) {
      formatters.set(key, new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        /*
         * Polish formatting writes "126 USD"; a trader reads "$". The dollar,
         * the euro and the pound get their sign in every language. Other codes
         * stay codes, where a bare "$" would not say which dollar it is.
         */
        currencyDisplay: SIGNED_CURRENCIES.has(code) ? 'narrowSymbol' : 'symbol',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }));
    }
    return formatters.get(key);
  };

  /**
   * @param {number} value
   * @param {number} [decimals]
   * @param {string} [currency] defaults to the account's base
   */
  const money = (value, decimals, currency) => {
    if (hideBalances) return '••••';
    const code = currency || baseCurrency;
    const dp = decimals || 0;
    try {
      // Intl renders the minus itself, in the position the locale expects.
      return formatterFor(code, dp).format(value);
    } catch {
      // An unrecognised code: show it plainly rather than throwing mid-render.
      const body = Math.abs(value).toLocaleString(locale, {
        minimumFractionDigits: dp, maximumFractionDigits: dp
      });
      return (value < 0 ? '−' : '') + body + ' ' + code;
    }
  };

  /**
   * One entry's own amount: grosze when it has them, none when it does not.
   *
   * Totals round to the złoty, which is right for a dashboard. A single entry
   * is a record of what happened, and 412,50 zł shown as 413 zł is a record
   * that is wrong -- while 8500 zł gains nothing from ",00".
   */
  const amount = (value, currency) =>
    money(value, Math.round(Math.abs(value) * 100) % 100 === 0 ? 0 : 2, currency);

  /** The bare symbol for a code, for compact labels and pickers. */
  const currencySymbol = code => {
    try {
      return formatterFor(code || baseCurrency, 0)
        .formatToParts(0)
        .filter(part => part.type === 'currency')
        .map(part => part.value)
        .join('') || code;
    } catch {
      return code;
    }
  };

  const number = (value, decimals) => value.toLocaleString(locale, {
    minimumFractionDigits: decimals || 0,
    maximumFractionDigits: decimals === undefined ? 4 : decimals
  });

  /** Always carries an explicit sign — used for deltas and P/L. */
  const signed = (value, currency) =>
    (value >= 0 ? '+' : '−') + money(Math.abs(value), 0, currency);

  /* A signed result to the cent when it has cents -- a trade's P&L, not a dashboard total. */
  /* Nothing gained or lost has no sign: a breakeven trade read "+$0". */
  const signedAmount = (value, currency) => {
    const shown = amount(Math.abs(value), currency);
    if (!/[1-9]/.test(shown)) return shown;
    return (value >= 0 ? '+' : '−') + shown;
  };

  /*
   * How a large number is shortened, in the reader's language. Polish writes
   * "4,5 tys." and "1,2 mln" -- "4,5k" is an English habit that read as a
   * slip on a Polish screen. The space before the word is a non-breaking one,
   * so a label never wraps between the number and its unit.
   */
  const scales = lang === 'PL'
    ? [[1e6, '\u00a0mln'], [1e3, '\u00a0tys.']]
    : [[1e6, 'M'], [1e3, 'k']];
  const shorten = value => {
    const [size, suffix] = scales.find(([limit]) => Math.abs(value) >= limit) || [1, ''];
    return { body: value / size, suffix };
  };

  /**
   * A short money label for chart captions.
   *
   * Hand-assembling this produced "zł556": the symbol was glued to the front
   * whatever the locale did with it, and the spacing rule only recognised
   * ASCII letters, so a złoty got neither the trailing position nor the space.
   * Intl knows where a currency goes in each locale; the only thing left to do
   * by hand is the thousands shortening.
   */
  const compact = value => {
    if (hideBalances) return '•••';

    const { body, suffix } = shorten(value);
    const short = Boolean(suffix);

    try {
      const text = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: baseCurrency,
        minimumFractionDigits: short ? 1 : 0,
        maximumFractionDigits: short ? 1 : 0
      }).format(short ? body : Math.round(body));

      // The unit rides with the digits, wherever in the string they sit.
      // Digits and their separators only: catching the space before a trailing
      // symbol would put the unit on the wrong side of it.
      return short ? text.replace(/([\d.,]+)(?!.*\d)/, '$1' + suffix) : text;
    } catch {
      const rounded = short ? body.toFixed(1) + suffix : String(Math.round(body));
      return rounded + ' ' + baseCurrency;
    }
  };

  /**
   * The same short number without the currency.
   *
   * Bar labels sit a few pixels apart and the currency is already stated once,
   * on the panel's scale line — repeated twelve times it is what makes two
   * neighbouring labels collide.
   */
  const compactParts = value => {
    if (hideBalances) return { number: '•••', unit: '' };

    const { body, suffix } = shorten(value);
    const short = Boolean(suffix);

    return {
      number: (short ? body : Math.round(body)).toLocaleString(locale, {
        minimumFractionDigits: short ? 1 : 0,
        maximumFractionDigits: short ? 1 : 0
      }),
      unit: suffix
    };
  };
  const compactPlain = value => {
    const { number, unit } = compactParts(value);
    return number + unit;
  };

  /*
   * In the reader's decimal separator. `toFixed` always writes a point, so the
   * Polish screen said "21.9%" a line away from "8,72 l/100 km" -- the one
   * place a number looked machine-written rather than typeset.
   */
  const percent = (ratio, decimals = 0) =>
    number(ratio * 100, decimals) + '%';

  const monthName = value => {
    const [year, month] = String(value).split('-');
    return t.months[parseInt(month, 10) - 1] + ' ' + year;
  };

  /**
   * A single day, in the translated month names the rest of the app uses.
   *
   * Not `toLocaleDateString`: that follows the browser's locale, not the
   * language the reader chose here, so a Polish UI on an English system would
   * print English months in one column and Polish ones everywhere else.
   */
  const day = value => {
    const [year, month, date] = String(value || '').split('-');
    if (!year || !month || !date) return '—';
    return parseInt(date, 10) + ' ' + t.months[parseInt(month, 10) - 1] + ' ' + year.slice(2);
  };

  const width = ratio => Math.round(Math.max(0, Math.min(1, ratio)) * 100) + '%';

  /** Bar heights keep the design's 6px floor so an empty month still reads. */
  const barHeight = (ratio, max) => Math.max(6, Math.round(ratio * max)) + 'px';

  // ---- vehicle units ------------------------------------------------------

  const imperial = unitSystem === 'imperial';
  const units = {
    system: unitSystem,
    distance: imperial ? t.units.mile : t.units.km,
    volume: imperial ? t.units.gallon : t.units.litre,
    economy: imperial ? t.units.mpg : t.units.per100,
    /*
     * Economy carries a decimal more in metric: L/100 km spans roughly 4–15,
     * where mpg spans 15–60, so the same precision reads as coarser.
     */
    distanceDp: 0,
    volumeDp: 1,
    economyDp: imperial ? 1 : 2
  };

  const distance = value => `${number(value, units.distanceDp)} ${units.distance}`;
  const volume = value => `${number(value, units.volumeDp)} ${units.volume}`;
  const economy = value =>
    value == null ? '—' : `${number(value, units.economyDp)} ${units.economy}`;
  const perDistance = value => `${money(value, 2)}/${units.distance}`;

  return {
    locale, baseCurrency, money, amount, signedAmount, currencySymbol, number, signed, compact, compactPlain, compactParts, percent,
    monthName, day, width, barHeight, units, distance, volume, economy, perDistance
  };
}
